import _ from 'lodash';
import { isHierarchySearchOutput } from '@/itemFinder/itemFinder.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import {
  getColumnsNotIncludedInAgGrid,
  getTableCellDisplayValue,
  isItemTableCell,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import { FilterTypeEnum } from '@seeqdev/api/model/ValueFilter';
import { SeeqNames } from '@seeqdev/constants';
import type {
  NavigableTableCell,
  ScalingTableRow,
  ScalingTableRowQuery,
  WithColumnName,
} from '@/tableDefinitionEditor/scalingTable.types';
import type { ItemTableCell, ProcessedTableCell, TableJoinData } from '@/tableDefinitionEditor/tableDefinition.types';

import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';
import type { TableResultOutputV1 } from '@seeqdev/api/model/models';

export const buildTableJoinDataForAliasColumns = (itemFinder: ItemFinderOutputV1): TableJoinData[] =>
  itemFinder.searches
    .filter(isHierarchySearchOutput)
    .filter((search) => !!search.hierarchy.aliasColumnId)
    .map((search): TableJoinData => {
      const hierarchy = search.hierarchy;
      const tableDefinition = hierarchy.tableDefinitionOutput;
      const aliasColumnId = hierarchy.aliasColumnId!;
      const aliasColumn = tableDefinition.columnDefinitions.find((col) => col.id === aliasColumnId);

      return {
        tableJoinInput: { tableDefinitionId: tableDefinition.id, columnIdsToInclude: [aliasColumnId] },
        aliasColumnId,
        aliasColumnName: aliasColumn!.columnName,
      };
    });

/**
 * Pure computation of what to request from the materialized table in order to fetch a scaling table row: which
 * UUID/navigation columns to include, which properties to fetch for each, the row-id equality filter, and any
 * table-join inputs needed for hierarchy alias columns. Kept free of network calls so the actual fetch can be
 * orchestrated from `scalingTable.api.ts` (see WEBSERVER_CODE_ORGANIZATION_SPEC.md's utilities-must-be-pure rule).
 * @param scalingTableDefinition - the scaling table's metadata (column definitions, table type, etc.)
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const buildScalingTableRowQuery = (
  scalingTableDefinition: TableDefinitionOutputV1,
  rowId: string,
): ScalingTableRowQuery => {
  const scalingTableIsCsv = scalingTableDefinition.tableType === TableTypeEnum.SCALINGCSV;
  const columnsNotIncludedInAgGrid = getColumnsNotIncludedInAgGrid(
    scalingTableDefinition.tableType,
    scalingTableDefinition.columnDefinitions,
  );
  const uuidColumns = scalingTableDefinition.columnDefinitions
    .filter((column) => column.columnType === ColumnTypeEnum.UUID)
    .filter((column) => !columnsNotIncludedInAgGrid.includes(column.columnName));

  const navigationColumn = scalingTableDefinition.navigationColumnId
    ? scalingTableDefinition.columnDefinitions.find((col) => col.id === scalingTableDefinition.navigationColumnId)
    : undefined;

  const columnNames = [
    ...uuidColumns.map((col) => col.columnName),
    ...(navigationColumn && !uuidColumns.some((col) => col.columnName === navigationColumn.columnName)
      ? [navigationColumn.columnName]
      : []),
  ];

  const propertyRequests: { uuidColumn: string; propertyNames: string[] }[] = uuidColumns.map((col) => ({
    uuidColumn: col.columnName,
    propertyNames: [SeeqNames.Properties.Name, SeeqNames.Properties.Type, SeeqNames.Properties.CreatedByModelInstance],
  }));

  if (navigationColumn && navigationColumn.columnType === ColumnTypeEnum.UUID) {
    const navIndex = propertyRequests.findIndex((req) => req.uuidColumn === navigationColumn.columnName);
    const displayProperty = navigationColumn.displayProperty || SeeqNames.Properties.Name;
    if (navIndex !== -1) {
      propertyRequests[navIndex].propertyNames = _.uniq([...propertyRequests[navIndex].propertyNames, displayProperty]);
    } else {
      propertyRequests.push({
        uuidColumn: navigationColumn.columnName,
        propertyNames: _.uniq([
          SeeqNames.Properties.Name,
          SeeqNames.Properties.Type,
          SeeqNames.Properties.CreatedByModelInstance,
          displayProperty,
        ]),
      });
    }
  }

  const tableJoinData = scalingTableDefinition.itemFinder
    ? buildTableJoinDataForAliasColumns(scalingTableDefinition.itemFinder)
    : [];

  return {
    columnNames,
    propertyRequests,
    filter: {
      valueFilter: {
        filterType: FilterTypeEnum.EQUALS,
        columnName: scalingTableIsCsv
          ? SeeqNames.MaterializedTables.CsvItemIdColumn
          : SeeqNames.MaterializedTables.ItemIdColumn,
        value: { value: rowId },
      },
    },
    tableJoinInputs: tableJoinData.length > 0 ? tableJoinData.map((data) => data.tableJoinInput) : undefined,
  };
};

/**
 * Pure shaping of an already-fetched materialized table result into a scaling table row: only item cells are kept
 * in `materializedTableRow`, while the navigation column's own cell is additionally returned as
 * `navigationColumnCell` regardless of whether it's a UUID or TEXT column (CRAB-59914: a TEXT navigation column's
 * cell would otherwise be silently dropped by the item-cell filter below).
 * @param scalingTableDefinition - the scaling table's metadata (column definitions, navigation column, etc.)
 * @param tableData - the raw materialized table result already fetched for this row
 */
export const buildScalingTableRowFromMaterializedTable = (
  scalingTableDefinition: TableDefinitionOutputV1,
  tableData: TableResultOutputV1 | undefined,
): ScalingTableRow => {
  const navigationColumn = scalingTableDefinition.navigationColumnId
    ? scalingTableDefinition.columnDefinitions.find((col) => col.id === scalingTableDefinition.navigationColumnId)
    : undefined;

  const processedMaterializedTable = processMaterializedTable(
    tableData,
    scalingTableDefinition.columnDefinitions.map((column) => {
      return {
        ...column,
        displayName: column.columnName,
        displayProperty: column.displayProperty,
        additionalProperties: [SeeqNames.Properties.CreatedByModelInstance],
      };
    }),
  );

  const headers = processedMaterializedTable?.headers;
  const processedRow: WithColumnName<ProcessedTableCell>[] = (processedMaterializedTable?.rows[0] ?? []).flatMap(
    (cell, index) => {
      const columnName = headers?.[index]?.name;
      return columnName === undefined ? [] : [{ ...cell, columnName }];
    },
  );

  return {
    materializedTableRow: processedRow.filter((cell): cell is WithColumnName<ItemTableCell> => isItemTableCell(cell)),
    // navigationColumn is guaranteed to be a UUID or TEXT column (columnMenu.utilities.ts's
    // COLUMN_NAVIGATION_SELECT menu option only offers those two types), so the matching cell is safely
    // narrowed to NavigableTableCell by columnName alone.
    navigationColumnCell: navigationColumn
      ? processedRow.find((cell): cell is NavigableTableCell => cell.columnName === navigationColumn.columnName)
      : undefined,
  };
};

/**
 * Resolves the display name for a scaling table row: the navigation column's value (whether it's a UUID or TEXT
 * column), falling back to the row's own item Name property when no navigation column is configured or it has no
 * resolvable value. CRAB-59914: shared by the Details Panel's "Source Scaling Table" row name and the scaling row
 * swap flow so both consistently prefer the navigation column over the row's raw item name.
 * @param scalingTableDefinition - the scaling table's metadata (column definitions, navigation column, etc.)
 * @param materializedTableRow - the row's item cells, as returned by `buildScalingTableRowFromMaterializedTable`
 * @param navigationColumnCell - the navigation column's own cell, whether it's a UUID or TEXT column
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const resolveScalingTableRowName = (
  scalingTableDefinition: TableDefinitionOutputV1,
  materializedTableRow: WithColumnName<ItemTableCell>[],
  navigationColumnCell: NavigableTableCell | undefined,
  rowId: string,
): string | undefined => {
  const navigationColumn = scalingTableDefinition.columnDefinitions.find(
    (column) => column.id === scalingTableDefinition.navigationColumnId,
  );
  const navigationRowName = navigationColumn
    ? getTableCellDisplayValue(navigationColumnCell, navigationColumn.displayProperty || SeeqNames.Properties.Name)
    : undefined;

  return (
    navigationRowName ??
    materializedTableRow.find((cell) => cell.uuid === rowId)?.fetchedProperties?.[SeeqNames.Properties.Name]
  );
};
